import { useMemo } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { InboxIcon, SortAscIcon, SortDescIcon, SortIcon, AlertIcon } from './icons';

export type SortDirection = 'asc' | 'desc';
export type ColumnAlign = 'left' | 'center' | 'right';
export type DataTableState = 'idle' | 'loading' | 'empty' | 'error' | 'no-results';

export interface DataTableColumn<TRow> {
  id: string;
  header: string;
  primary?: boolean;
  minWidth?: number;
  align?: ColumnAlign;
  sortable?: boolean;
  render: (row: TRow) => ReactNode;
  cellClassName?: string;
}

interface EmptyConfig {
  icon?: ReactNode;
  message: string;
  cta?: ReactNode;
}

interface NoResultsConfig {
  icon?: ReactNode;
  message?: string;
}

interface DataTableProps<TRow> {
  columns: DataTableColumn<TRow>[];
  rows: TRow[];
  getRowId: (row: TRow) => string;

  state?: DataTableState;
  errorMessage?: string;
  onRetry?: () => void;
  emptyState?: EmptyConfig;
  noResultsState?: NoResultsConfig;

  sort?: { columnId: string; direction: SortDirection };
  onSortChange?: (columnId: string, direction: SortDirection) => void;

  selectedRowId?: string | null;
  onRowClick?: (row: TRow) => void;

  renderRowActions?: (row: TRow) => ReactNode;
  loadingRowCount?: number;
  ariaLabel: string;
}

const DEFAULT_PRIMARY_MIN_WIDTH = 240;
const DEFAULT_MIN_WIDTH = 100;

/**
 * Generic table primitive aligned to Jira OOM-1259.
 *
 * - No data-fetching, no internal pagination. Consumers own pagination via
 *   `TablePagination` rendered inside `StandardPage` footer.
 * - States (idle / loading / empty / error / no-results) are mutually exclusive.
 * - If `state` is omitted, it auto-promotes to `'empty'` when rows are empty.
 *
 * Why we don't use HTML `<table>` directly:
 *  We use ARIA-roled `<div>` rows to allow per-row hover overlays and the
 *  3-dot action menu to escape the row's overflow context while preserving
 *  accessible table semantics via `role="table" / "row" / "cell"`.
 */
export function DataTable<TRow>({
  columns,
  rows,
  getRowId,
  state,
  errorMessage,
  onRetry,
  emptyState,
  noResultsState,
  sort,
  onSortChange,
  selectedRowId,
  onRowClick,
  renderRowActions,
  loadingRowCount = 6,
  ariaLabel,
}: DataTableProps<TRow>): JSX.Element {
  const effectiveState: DataTableState = useMemo(() => {
    if (state) return state;
    return rows.length === 0 ? 'empty' : 'idle';
  }, [state, rows.length]);

  const gridTemplate = useMemo(
    () =>
      [
        ...columns.map((c) => {
          const min = c.minWidth ?? (c.primary ? DEFAULT_PRIMARY_MIN_WIDTH : DEFAULT_MIN_WIDTH);
          return `minmax(${min}px, ${c.primary ? '2fr' : '1fr'})`;
        }),
        renderRowActions ? '48px' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [columns, renderRowActions],
  );

  const handleSortClick = (column: DataTableColumn<TRow>): void => {
    if (!column.sortable || !onSortChange) return;
    const isActive = sort?.columnId === column.id;
    const nextDirection: SortDirection =
      isActive && sort?.direction === 'asc' ? 'desc' : 'asc';
    onSortChange(column.id, nextDirection);
  };

  return (
    <div
      className="bui-scope flex w-full flex-col overflow-hidden"
      style={{
        backgroundColor: 'var(--bui-surface)',
        boxShadow: 'var(--bui-shadow-table)',
        borderRadius: 'var(--bui-radius-container)',
      }}
    >
      {effectiveState === 'error' && (
        <ErrorBanner message={errorMessage ?? 'Something went wrong.'} onRetry={onRetry} />
      )}

      <div
        role="table"
        aria-label={ariaLabel}
        aria-rowcount={rows.length}
        className="flex min-w-0 flex-col"
      >
        <div
          role="row"
          className="grid items-center px-4"
          style={{
            gridTemplateColumns: gridTemplate,
            backgroundColor: 'var(--bui-surface-alt)',
            borderBottom: '1px solid var(--bui-divider)',
            minHeight: 36,
          }}
        >
          {columns.map((column) => {
            const isSorted = sort?.columnId === column.id;
            const ariaSort = isSorted ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : 'none';
            return (
              <div
                key={column.id}
                role="columnheader"
                aria-sort={ariaSort}
                className={clsx(
                  'flex items-center gap-1.5 py-2 text-xs font-semibold uppercase tracking-wide',
                  column.align === 'center' && 'justify-center',
                  column.align === 'right' && 'justify-end',
                )}
                style={{ color: 'var(--bui-accent)' }}
              >
                {column.sortable ? (
                  <button
                    type="button"
                    onClick={() => handleSortClick(column)}
                    className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2"
                    aria-label={`Sort by ${column.header}`}
                  >
                    <span>{column.header}</span>
                    <SortIndicator active={isSorted} direction={sort?.direction} />
                  </button>
                ) : (
                  <span>{column.header}</span>
                )}
              </div>
            );
          })}
          {renderRowActions && <span aria-hidden className="block" />}
        </div>

        {effectiveState === 'loading' && (
          <SkeletonRows
            columns={columns}
            count={loadingRowCount}
            gridTemplate={gridTemplate}
            hasActions={Boolean(renderRowActions)}
          />
        )}

        {effectiveState === 'idle' &&
          rows.map((row) => {
            const rowId = getRowId(row);
            const isSelected = selectedRowId === rowId;
            return (
              <div
                key={rowId}
                role="row"
                aria-selected={isSelected}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : -1}
                className={clsx(
                  'group grid items-center px-4 transition-colors',
                  onRowClick && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2',
                )}
                style={{
                  gridTemplateColumns: gridTemplate,
                  borderBottom: '1px solid var(--bui-divider)',
                  minHeight: 38,
                  backgroundColor: isSelected
                    ? 'var(--bui-surface-selected)'
                    : 'var(--bui-surface)',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'var(--bui-surface-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected
                    ? 'var(--bui-surface-selected)'
                    : 'var(--bui-surface)';
                }}
              >
                {columns.map((column) => {
                  const isActive = sort?.columnId === column.id;
                  return (
                    <div
                      key={column.id}
                      role="cell"
                      className={clsx(
                        'flex min-w-0 items-center py-2 text-sm',
                        column.align === 'center' && 'justify-center',
                        column.align === 'right' && 'justify-end',
                        column.cellClassName,
                      )}
                      style={{
                        color:
                          column.primary || isActive
                            ? 'var(--bui-text-primary)'
                            : 'var(--bui-text-secondary)',
                      }}
                    >
                      <TruncatedCell>{column.render(row)}</TruncatedCell>
                    </div>
                  );
                })}
                {renderRowActions && (
                  <div role="cell" className="flex items-center justify-center">
                    <div
                      className={clsx(
                        'opacity-0 transition-opacity',
                        'group-hover:opacity-100 group-focus-within:opacity-100',
                      )}
                    >
                      {renderRowActions(row)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        {effectiveState === 'empty' && (
          <CenteredState icon={emptyState?.icon ?? <InboxIcon />} message={emptyState?.message ?? 'No data yet.'} cta={emptyState?.cta} />
        )}

        {effectiveState === 'no-results' && (
          <CenteredState
            icon={noResultsState?.icon ?? <InboxIcon />}
            message={noResultsState?.message ?? 'No results found.'}
          />
        )}
      </div>
    </div>
  );
}

function SortIndicator({ active, direction }: { active: boolean; direction?: SortDirection }): JSX.Element {
  if (!active) {
    return <SortIcon size={12} style={{ color: 'var(--bui-text-placeholder)' }} />;
  }
  return direction === 'desc' ? (
    <SortDescIcon size={12} style={{ color: 'var(--bui-accent)' }} />
  ) : (
    <SortAscIcon size={12} style={{ color: 'var(--bui-accent)' }} />
  );
}

function TruncatedCell({ children }: { children: ReactNode }): JSX.Element {
  const titleString = typeof children === 'string' || typeof children === 'number' ? String(children) : undefined;
  return (
    <span className="block w-full min-w-0 truncate" title={titleString}>
      {children}
    </span>
  );
}

function SkeletonRows<TRow>({
  columns,
  count,
  gridTemplate,
  hasActions,
}: {
  columns: DataTableColumn<TRow>[];
  count: number;
  gridTemplate: string;
  hasActions: boolean;
}): JSX.Element {
  return (
    <>
      {Array.from({ length: count }, (_, idx) => (
        <div
          key={idx}
          role="row"
          aria-busy="true"
          className="grid items-center px-4"
          style={{
            gridTemplateColumns: gridTemplate,
            borderBottom: '1px solid var(--bui-divider)',
            minHeight: 38,
          }}
        >
          {columns.map((column) => (
            <div key={column.id} role="cell" className="flex items-center py-2">
              <span
                className="block h-3 w-3/4 animate-pulse rounded"
                style={{ backgroundColor: 'var(--bui-divider)' }}
              />
            </div>
          ))}
          {hasActions && <span aria-hidden />}
        </div>
      ))}
    </>
  );
}

function CenteredState({
  icon,
  message,
  cta,
}: {
  icon: ReactNode;
  message: string;
  cta?: ReactNode;
}): JSX.Element {
  return (
    <div
      role="row"
      className="flex flex-col items-center justify-center gap-3 py-10 text-center"
      style={{ color: 'var(--bui-text-secondary)' }}
    >
      <span style={{ color: 'var(--bui-text-placeholder)' }}>{icon}</span>
      <p className="text-sm">{message}</p>
      {cta}
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }): JSX.Element {
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 px-4 py-2 text-sm"
      style={{
        backgroundColor: 'var(--bui-danger-soft)',
        color: 'var(--bui-danger)',
        borderBottom: '1px solid var(--bui-divider)',
      }}
    >
      <span className="inline-flex items-center gap-2">
        <AlertIcon />
        {message}
      </span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md border px-2 py-1 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2"
          style={{ borderColor: 'var(--bui-danger)', color: 'var(--bui-danger)' }}
        >
          Retry
        </button>
      )}
    </div>
  );
}
